import { Menu, Transition } from '@headlessui/react';
import { getTestSelector } from '@oncolens/oncolens-ui/lib/utilities/tests';
import clsx from 'clsx';
import * as React from 'react';

const variants = {
  blue: 'bg-oncolensBlue-600 text-white hover:bg-oncolensBlue-700 focus:bg-oncolensBlue-500 border-transparent',
  gray: 'text-gray-700 bg-white hover:bg-gray-50 focus:ring-oncolensBlue-500 border-gray-300',
  red: 'bg-red-500 text-white hover:bg-red-600 focus:ring-red-500 border-transparent'
};

const sizes = {
  sm: 'py-2 px-2 text-sm',
  md: 'py-3 px-6 text-md',
  lg: 'py-4 px-8 text-lg'
};

const alignments = {
  left: 'left-0',
  right: 'right-0'
};

type IconProps =
	| { startIcon: React.ReactElement; endIcon?: never }
	| { endIcon: React.ReactElement; startIcon?: never }
	| { endIcon?: undefined; startIcon?: undefined };

type MenuItem = {
	label: string;
	onClick: () => void;
	icon?: IconProps;
};

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
	variant?: keyof typeof variants;
	size?: keyof typeof sizes;
	alignment?: keyof typeof alignments;
	menuItems: MenuItem[];
	testSelector?: string;
} & IconProps;

export const ButtonMenu = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = 'button',
      className = '',
      variant = 'blue',
      size = 'md',
      alignment = 'left',
      menuItems,
      startIcon,
      endIcon,
      children,
      testSelector = getTestSelector(
        typeof children === 'string' ? children : 'not-identified',
        'menu-button'
      ),
      ...props
    },
    ref
  ) => {
    return (
      <Menu as="div" className="relative print:hidden">
        <div>
          <Menu.Button
            ref={ref}
            type={type}
            className={clsx(
              'inline-flex items-center border shadow-sm text-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:bg-blue-100',
              variants[variant],
              sizes[size],
              className
            )}
            data-cy={testSelector}
            {...props}
          >
            <span className="sr-only">Open menu</span>
            {startIcon}
            <span className="mx-2">{children}</span> {endIcon}
          </Menu.Button>
        </div>
        <Transition
          as={React.Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items
            className={clsx(
              alignments[alignment],
              'absolute z-20 py-1 mt-2 origin-top-left bg-white rounded-md shadow-lg ring-1 max-w-prose ring-black ring-opacity-5 focus:outline-none'
            )}
          >
            {menuItems.map((item) => (
              <Menu.Item key={item.label}>
                {({ active }) => (
                  <a
                    href="#"
                    onClick={(evt) => {
                      evt.preventDefault();
                      item.onClick();
                    }}
                    className={clsx(
                      active ? 'bg-gray-100' : '',
                      'flex items-center px-4 py-2 text-sm text-gray-700',
                      item.label.length < 65 && 'whitespace-nowrap'
                    )}
                    data-cy={getTestSelector('option', testSelector)}
                  >
                    {item.icon?.startIcon}
                    {item.label}
                    {item.icon?.endIcon}
                  </a>
                )}
              </Menu.Item>
            ))}
          </Menu.Items>
        </Transition>
      </Menu>
    );
  }
);

ButtonMenu.displayName = 'Button';
